import { useEffect, useState } from "react";
import { Radar, ShieldAlert, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// v38: single Login screen for both auth providers. We ask the backend
// which provider is active (/auth/config) and route the Google button
// accordingly:
//   - provider="emergent" → send user to auth.emergentagent.com, same as pre-v38
//   - provider="kolradar" → ask the backend to build the Google authorize
//     URL (with state + PKCE) and send the user there. Google's sign-in
//     screen then reads "KOL Radar" instead of "Emergent".
// Flip back to the Emergent path is a server-side env change — no code
// change here. The frontend keeps a tiny loading state while the config
// resolves so the user never sees a half-wired button.
export default function Login() {
  const [signinError, setSigninError] = useState(null);
  const [provider, setProvider] = useState(null); // null = loading
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem("signin_error");
      if (msg) {
        setSigninError(msg);
        sessionStorage.removeItem("signin_error");
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    let dead = false;
    api.get("/auth/config")
      .then((r) => { if (!dead) setProvider(r.data?.provider || "emergent"); })
      .catch(() => { if (!dead) setProvider("emergent"); });
    return () => { dead = true; };
  }, []);

  const handleLogin = async () => {
    if (!provider || starting) return;
    setStarting(true);
    try {
      if (provider === "kolradar") {
        // KOL Radar's own OAuth client. The backend builds the Google URL
        // with state + PKCE and we only need to redirect to it. We echo
        // back the exact redirect_uri the backend used so it can match
        // what Google will send on the way back.
        const redirect_uri = window.location.origin + "/auth/google";
        const { data } = await api.get("/auth/google/start", { params: { redirect_uri } });
        if (!data?.authorize_url) throw new Error("Backend did not return an authorize URL");
        window.location.href = data.authorize_url;
        return;
      }
      // Emergent-managed Google sign-in — unchanged pre-v38 flow.
      const redirectUrl = window.location.origin + "/search";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (e) {
      setSigninError(e?.response?.data?.detail || e?.message || "Sign-in could not start — please try again.");
      setStarting(false);
    }
  };

  const buttonDisabled = provider === null || starting;

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: "var(--bg)" }}>
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div
        className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 60%)" }}
      />
      <div
        className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-15"
        style={{ background: "radial-gradient(circle, #39FF14 0%, transparent 60%)" }}
      />

      <div className="relative z-10 w-full max-w-md p-8 border hairline rounded-lg" style={{ background: "var(--surface)" }}>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <Radar size={20} color="#0A0A0B" />
          </div>
          <div>
            <div className="display text-xl font-semibold">KOL Radar</div>
            <div className="text-xs text-zinc-500 mono uppercase tracking-[0.2em]">Access Terminal</div>
          </div>
        </div>

        {signinError && (
          <div
            data-testid="signin-error-banner"
            className="mb-6 flex items-start gap-3 p-3 rounded-md border"
            style={{ background: "rgba(244,63,94,0.08)", borderColor: "rgba(244,63,94,0.35)", color: "#F87171" }}
          >
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            <div className="text-sm leading-snug">{signinError}</div>
          </div>
        )}

        <h1 className="display text-2xl leading-tight mb-2">Sign in to access market intelligence</h1>
        <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
          KOL Radar is restricted to authorized crypto exchange growth teams. Use your Google account to continue.
        </p>

        <button
          data-testid="google-login-btn"
          data-auth-provider={provider || "loading"}
          onClick={handleLogin}
          disabled={buttonDisabled}
          className="w-full flex items-center justify-center gap-3 py-3 rounded-md font-medium text-[15px] transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
          style={{ background: "var(--primary)", color: "#0A0A0B" }}
        >
          {starting ? (
            <><Loader2 size={16} className="animate-spin" /> Taking you to Google…</>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#0A0A0B" d="M24 9.5c3.9 0 6.6 1.7 8.1 3.1l5.9-5.7C34.4 3.4 29.7 1.5 24 1.5 14.6 1.5 6.5 6.9 2.8 14.9l7 5.4C11.6 14.2 17.3 9.5 24 9.5z"/><path fill="#0A0A0B" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 2.8-2 5.2-4.4 6.8l6.8 5.3c4-3.7 6.4-9.1 6.4-16.6z"/><path fill="#0A0A0B" d="M9.8 28.7c-.5-1.4-.7-2.8-.7-4.2s.3-2.8.7-4.2l-7-5.4C1.8 17.9 1 20.8 1 24s.8 6.1 2 8.9l6.8-4.2z"/><path fill="#0A0A0B" d="M24 46.5c5.7 0 10.5-1.9 14-5.2l-6.8-5.3c-1.9 1.3-4.3 2-7.2 2-6.7 0-12.4-4.7-14.2-11l-7 5.4C6.5 41.1 14.6 46.5 24 46.5z"/></svg>
              Continue with Google
            </>
          )}
        </button>

        <p className="mt-6 text-[11px] text-zinc-600 mono uppercase tracking-[0.15em]">
          Read-only • Team access only
        </p>
      </div>
    </div>
  );
}
